import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import DocumentHeader from 'Components/DocumentHeader';
import DocumentWrapper from 'Components/DocumentWrapper';
import HistorySection from 'Components/HistorySection';
import HistoryRow from 'Components/HistoryRow';
import HistoryRowControls from 'Components/HistoryRowControls';
import FormEditingHandler from 'Containers/FormEditingHandler';
import HistoryRowData from './HistoryRowData';
import Form from './Form';
import { sortPartialDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import { isEnded } from '../utils';
import DocumentCreationButton from 'Components/DocumentCreationButton';

const ongoingSymptoms = (documents: IDocumentWithTypeV2<'otherSymptoms'>[], startEdit: StartEdit): React.ReactNode => {
  return documents.length > 0 ? (
    <HistorySection headerText={<FormattedMessage id="otherSymptoms.ongoingSymptoms" />} hasHistoryRows={true}>
      {documents
        .toSorted((n1, n2) => sortPartialDate(n2.date, n1.date) || n2._cdate - n1._cdate)
        .map((d, i) => (
          <HistoryRow
            headerText={
              d.date ? (
                <FormattedMessage id="otherSymptoms.startingDate" values={{ date: formatPartialDate(d.date) }} />
              ) : undefined
            }
            key={i + 'active'}
            rowButton={<HistoryRowControls document={d} startEdit={startEdit} />}
            controlsMargin={false}
          >
            <HistoryRowData document={d} />
          </HistoryRow>
        ))}
    </HistorySection>
  ) : null;
};

const disappearedSymptoms = (
  documents: IDocumentWithTypeV2<'otherSymptoms'>[],
  startEdit: StartEdit,
): React.ReactNode => {
  return documents.length > 0 ? (
    <HistorySection
      headerText={<FormattedMessage id="otherSymptoms.disappearedSymptoms" />}
      inactive={true}
      hasHistoryRows={true}
    >
      {documents
        .toSorted((n1, n2) => sortPartialDate(n2.endDate, n1.endDate) || n2._cdate - n1._cdate)
        .map((d, i) => (
          <HistoryRow
            headerText={
              d.date ? formatPartialDate(d.date) + ' - ' + (d.endDate ? formatPartialDate(d.endDate) : '') : undefined
            }
            key={i + 'active'}
            rowButton={<HistoryRowControls document={d} startEdit={startEdit} />}
            controlsMargin={false}
          >
            <HistoryRowData document={d} />
          </HistoryRow>
        ))}
    </HistorySection>
  ) : null;
};

const OtherSymptoms = ({ documents }: IOwnProps): React.JSX.Element => (
  <FormEditingHandler name="otherSymptoms" documents={documents}>
    {(editing, startEdit, formData): React.JSX.Element => (
      <DocumentWrapper>
        <DocumentHeader
          name="otherSymptoms"
          headerId={'otherSymptoms.title'}
          editing={editing}
          editButtons={
            <div>
              <DocumentCreationButton name="otherSymptoms" text={'general.new'} onClick={startEdit({})} />
            </div>
          }
        />
        {!editing && documents.length ? (
          <div>
            {ongoingSymptoms(
              documents.filter(d => !isEnded(d.endDate)),
              startEdit,
            )}
            {disappearedSymptoms(
              documents.filter(d => isEnded(d.endDate)),
              startEdit,
            )}
          </div>
        ) : undefined}
        {editing ? (
          <Form formData={formData} editing={editing} document={documents.find(d => d._id === editing)} />
        ) : undefined}
      </DocumentWrapper>
    )}
  </FormEditingHandler>
);

interface IOwnProps {
  documents: IDocumentWithTypeV2<'otherSymptoms'>[];
}

export default OtherSymptoms;
